import { ANDROID_URL, IOS_URL } from '@goodfellas/environment'

/**
 * Links e identificadores do app (sweep). Fonte da verdade dos IDs:
 * - Android: `sweep/android/app/build.gradle.kts` (`com.goodfellas.sweepapp`)
 * - iOS: `sweep/ios/Runner.xcodeproj` (`com.goodfellas.app`, Team U2C523K8NM)
 *
 * Destinos oficiais de download:
 * - Android: Play Store (`PLAY_STORE_URL`).
 * - iOS: enquanto o app não tem listagem pública na App Store, o destino
 *   oficial é o TestFlight (`TESTFLIGHT_URL`). Use `IOS_DOWNLOAD_URL` para
 *   resolver o destino iOS correto (App Store se/quando houver, senão
 *   TestFlight). `APP_STORE_ID`/`APP_STORE_URL` ficam reservados para o Smart
 *   App Banner e só existem quando houver uma listagem pública real.
 *
 * `GOODFELLAS_ANDROID_URL`/`GOODFELLAS_IOS_URL` podem sobrescrever via env,
 * mas valores stale/placeholder (esquema `market://`, pacote antigo, App Store
 * ID fictício `1550000000`) são normalizados para os defaults corretos.
 */
export const ANDROID_PACKAGE = 'com.goodfellas.sweepapp'
export const IOS_BUNDLE_ID = 'com.goodfellas.app'

const DEFAULT_PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`

/** Link oficial do TestFlight (distribuição iOS atual, sem listagem na App Store). */
export const TESTFLIGHT_URL = 'https://testflight.apple.com/join/vSgxPFdZ'

/** App Store ID de placeholder usado em `.env.development` — não é real. */
const PLACEHOLDER_APP_STORE_ID = '1550000000'

function normalizePlayStoreUrl(url?: string): string {
  if (!url || !url.startsWith('https://')) return DEFAULT_PLAY_STORE_URL
  if (!url.includes(ANDROID_PACKAGE)) return DEFAULT_PLAY_STORE_URL
  return url
}

function extractAppStoreId(url?: string): string | null {
  const match = url?.match(/id(\d{6,})/)
  if (!match || match[1] === PLACEHOLDER_APP_STORE_ID) return null
  return match[1]
}

export const PLAY_STORE_URL = normalizePlayStoreUrl(ANDROID_URL)

/**
 * App Store ID real, ou `null` enquanto não houver listagem pública. Usado só
 * pelo Smart App Banner (`AppleSmartBanner`), que exige um ID numérico — o
 * TestFlight não alimenta o banner nativo.
 */
export const APP_STORE_ID = extractAppStoreId(IOS_URL)

/** `null` enquanto não houver listagem pública na App Store (o iOS usa o TestFlight; ver `IOS_DOWNLOAD_URL`). */
export const APP_STORE_URL = APP_STORE_ID
  ? `https://apps.apple.com/app/id${APP_STORE_ID}`
  : null

/**
 * Destino oficial de download para iOS: App Store quando houver listagem
 * pública, senão TestFlight. Sempre resolve para uma URL `https` válida.
 */
export const IOS_DOWNLOAD_URL = APP_STORE_URL ?? TESTFLIGHT_URL

/**
 * Loja do celular que abriu o link. `null` no desktop, onde não dá para
 * escolher Play Store ou App Store pelo user agent.
 */
export function downloadUrlForUserAgent(userAgent: string): string | null {
  const ua = userAgent.toLowerCase()
  if (ua.includes('android')) return PLAY_STORE_URL
  if (/iphone|ipad|ipod/.test(ua)) return IOS_DOWNLOAD_URL
  return null
}

/**
 * URL `intent://` para Android: abre o app se instalado (App Link) e cai na
 * Play Store caso contrário. Recebe a URL https absoluta do conteúdo.
 */
export function androidIntentUrl(absoluteUrl: string): string {
  const withoutScheme = absoluteUrl.replace(/^https?:\/\//, '')
  return (
    `intent://${withoutScheme}#Intent;scheme=https;` +
    `package=${ANDROID_PACKAGE};` +
    `S.browser_fallback_url=${encodeURIComponent(PLAY_STORE_URL)};end`
  )
}
